import { useCallback, useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { api, ApiError } from "@/lib/api";
import { useAuthToken } from "@/lib/auth";
import { onDashboardChanged } from "@/lib/dashboardWs";

interface AvailableVersion {
  version: string;
  publishedAt: string | null;
}
interface FleetBoxStatus {
  box_id: string;
  hostname: string;
  installed_version: string | null;
  last_seen_at: string;
}
interface DependencyRow {
  id: string;
  category: "infra" | "fleet" | "project";
  display_name: string;
  component_group: string;
  source_repo: string;
  current_version: string | null;
  available_versions?: AvailableVersion[];
  latest_published?: string | null;
  boxes?: FleetBoxStatus[];
}
interface PropagateInstruction {
  repo: string;
  path: string;
  changes: {
    display_name: string;
    from: string | null;
    to: string;
    line: string | null;
  }[];
}

function errMsg(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}

export function DependenciesPage() {
  const token = useAuthToken();
  const call = useCallback(
    <T,>(path: string, init?: RequestInit) => api<T>(token, path, init),
    [token],
  );

  const [deps, setDeps] = useState<DependencyRow[] | null>(null);
  const [selections, setSelections] = useState<Record<string, string>>({});
  const [refreshing, setRefreshing] = useState(false);
  const [instructions, setInstructions] = useState<
    PropagateInstruction[] | null
  >(null);
  const [error, setError] = useState<string | null>(null);
  const [propagateError, setPropagateError] = useState<string | null>(null);

  const load = useCallback(() => {
    call<{ ok: true; dependencies: DependencyRow[] }>("/dependencies")
      .then((r) => {
        setDeps(r.dependencies);
        setSelections({});
      })
      .catch((err) => setError(errMsg(err, "failed to load dependencies")));
  }, [call]);

  useEffect(load, [load]);
  useEffect(() => onDashboardChanged(token, load), [token, load]);

  async function refreshAvailable() {
    setRefreshing(true);
    try {
      await call("/dependencies/refresh", { method: "POST" });
      await load();
    } catch (err) {
      setError(errMsg(err, "failed to refresh available versions"));
    } finally {
      setRefreshing(false);
    }
  }

  async function propagate() {
    const updates = Object.entries(selections).map(([id, version]) => ({
      id,
      version,
    }));
    if (!updates.length) {
      setPropagateError(
        "Select at least one dependency and a target version first.",
      );
      return;
    }
    setPropagateError(null);
    try {
      const res = await call<{
        ok: true;
        instructions: PropagateInstruction[];
      }>("/dependencies/propagate", {
        method: "POST",
        body: JSON.stringify({ updates }),
      });
      setInstructions(res.instructions);
    } catch (err) {
      setPropagateError(errMsg(err, "failed to propagate"));
    }
  }

  const infra = deps?.filter((d) => d.category === "infra") ?? null;
  const fleet = deps?.filter((d) => d.category === "fleet") ?? null;
  const project = deps?.filter((d) => d.category === "project") ?? null;

  return (
    <div className="max-w-5xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Dependencies</h1>
        <p className="text-sm text-muted-foreground">
          Infra pins, fleet component versions, and project toolchains.
        </p>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <Card>
        <CardHeader>
          <CardTitle>Infrastructure dependencies</CardTitle>
          <CardDescription>
            Third-party, fleet-wide pins (Firecracker, kernel, Rust, SPIRE, …).
            Pick a new version for one or more rows, then Propagate — this does
            NOT deploy anything itself; it returns the exact edit to make in{" "}
            <code className="bg-muted px-1 py-0.5 rounded">versions.env</code>,
            for you to apply and open an MR with by hand. Once merged, each
            box's own update timer picks it up on its own.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Dependency</TableHead>
                <TableHead>Component</TableHead>
                <TableHead>Current</TableHead>
                <TableHead>Update to</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {infra?.map((d) => (
                <TableRow key={d.id}>
                  <TableCell>{d.display_name}</TableCell>
                  <TableCell>{d.component_group}</TableCell>
                  <TableCell className="font-mono text-xs">
                    {d.current_version ?? "?"}
                  </TableCell>
                  <TableCell>
                    <Select
                      value={selections[d.id] ?? ""}
                      onChange={(e) =>
                        setSelections((cur) => {
                          if (!e.target.value) {
                            const { [d.id]: _drop, ...rest } = cur;
                            return rest;
                          }
                          return { ...cur, [d.id]: e.target.value };
                        })
                      }
                    >
                      <option value="">
                        (keep {d.current_version ?? "unknown"})
                      </option>
                      {(d.available_versions ?? []).map((v) => (
                        <option key={v.version} value={v.version}>
                          {v.version}
                        </option>
                      ))}
                      {!(d.available_versions ?? []).length && (
                        <option value="" disabled>
                          (no versions discovered yet — try refresh)
                        </option>
                      )}
                    </Select>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {infra?.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No infrastructure dependencies known.
            </p>
          )}

          <div className="flex flex-wrap items-center gap-3 mt-3">
            <Button onClick={propagate}>Propagate selected</Button>
            <button
              type="button"
              className="text-sm text-primary hover:underline disabled:opacity-50"
              disabled={refreshing}
              onClick={refreshAvailable}
            >
              {refreshing ? "↻ refreshing…" : "↻ refresh available versions"}
            </button>
          </div>

          {propagateError && (
            <p className="text-sm text-destructive mt-2">{propagateError}</p>
          )}

          {instructions && (
            <div className="space-y-3 mt-3">
              {instructions.map((instr) => (
                <div
                  key={instr.repo + instr.path}
                  className="bg-muted p-3 rounded-md font-mono text-xs whitespace-pre-wrap"
                >
                  {instr.repo} : {instr.path}
                  {"\n\n"}
                  {instr.changes
                    .map(
                      (c) =>
                        `# ${c.display_name}: ${c.from ?? "?"} -> ${c.to}\n${c.line ?? ""}`,
                    )
                    .join("\n")}
                </div>
              ))}
              <p className="text-xs text-muted-foreground">
                Apply these edits in the file(s) shown and open your own MR —
                nothing has been changed yet.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Fleet component versions</CardTitle>
          <CardDescription>
            First-party services with their own repo/CI, always "latest".
            Read-only: what each box actually has installed vs. what CI most
            recently published.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Component</TableHead>
                <TableHead>Latest published</TableHead>
                <TableHead>Boxes up to date</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {fleet?.map((d) => {
                const boxes = d.boxes ?? [];
                const upToDate = boxes.filter(
                  (b) =>
                    b.installed_version &&
                    b.installed_version === d.latest_published,
                ).length;
                return (
                  <TableRow key={d.id}>
                    <TableCell>{d.display_name}</TableCell>
                    <TableCell className="font-mono text-xs">
                      {d.latest_published ?? "unknown"}
                    </TableCell>
                    <TableCell>
                      {boxes.length
                        ? `${upToDate} / ${boxes.length}`
                        : "no boxes connected"}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {fleet?.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No fleet components known.
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Project build-toolchain pins</CardTitle>
          <CardDescription>
            Each project's own TypeScript/Node/Rust toolchain, self-managed
            within that project's own repo. Read-only.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Project</TableHead>
                <TableHead>Dependency</TableHead>
                <TableHead>Current</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {project?.map((d) => (
                <TableRow key={d.id}>
                  <TableCell>{d.source_repo}</TableCell>
                  <TableCell>{d.display_name}</TableCell>
                  <TableCell className="font-mono text-xs">
                    {d.current_version ?? "not yet reported"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {project?.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No project dependencies known.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
